import { useState, useRef, useEffect } from "react";

function CollapseView({
  stock,
  onApproveReceive,
  onApproveRequest,
  onApprovePartialRequest,
  onApproveReconcile,
  onDispatchRequest,
  onEdit,
  onClickAction,
}) {
  console.log("Stock", stock);
  const [showDisapprove, setShowDisapprove] = useState(false);
  const menuRef = useRef(null);

  const handleToggle = () => setShowDisapprove((prev) => !prev);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowDisapprove(false);
      }
    };
    if (showDisapprove) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDisapprove]);

  const formatDate = (date) =>
    date &&
    new Date(date).toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const statusColors = {
    Pending: { bg: "#fff8e1", color: "#f59e0b", border: "#fcd34d" },
    Approved: { bg: "#edf7e1", color: "#82c14a", border: "#a3d977" },
    Dispatched: { bg: "#e0f2fe", color: "#0284c7", border: "#7dd3fc" },
    "Partially Received": {
      bg: "#fef3c7",
      color: "#d97706",
      border: "#fbbf24",
    },
    "Pending Fully Received": {
      bg: "#ede9fe",
      color: "#7c3aed",
      border: "#a78bfa",
    },
    "Pending Partially Received": {
      bg: "#fef3c7",
      color: "#d97706",
      border: "#fbbf24",
    },
    "For Reconciliation": {
      bg: "#fce7f3",
      color: "#db2777",
      border: "#f9a8d4",
    },
    Completed: { bg: "#d1fae5", color: "#059669", border: "#6ee7b7" },
    Cancelled: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    Disapproved: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
  };

  const statusStyle = statusColors[stock?.status] || {
    bg: "#f3f4f6",
    color: "#6b7280",
    border: "#d1d5db",
  };

  const InfoRow = ({ icon, label, value }) =>
    value ? (
      <div className="d-flex align-items-center gap-2 mb-2">
        <i
          className={`bi ${icon}`}
          style={{ color: "#82c14a", fontSize: "0.9rem", width: "18px" }}
        />
        <span
          className="text-muted"
          style={{ fontSize: "0.8rem", minWidth: "110px" }}
        >
          {label}
        </span>
        <span className="fw-semibold text-dark" style={{ fontSize: "0.85rem" }}>
          {value}
        </span>
      </div>
    ) : null;

  const QtySection = ({ title, icon, items, valueKey }) => (
    <div
      className="rounded-3 p-3 flex-fill"
      style={{ background: "#f9fafb", border: "1px solid #f0f0f0" }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <i
          className={`bi ${icon}`}
          style={{ color: "#82c14a", fontSize: "0.85rem" }}
        />
        <span
          className="fw-bold"
          style={{ fontSize: "0.8rem", color: "#374151" }}
        >
          {title}
        </span>
      </div>
      {(items || []).map((item) => (
        <div
          key={item.sku}
          className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2 mb-1"
          style={{ background: "#fff", fontSize: "0.8rem" }}
        >
          <span className="text-dark">{item.itemName}</span>
          <span
            className="fw-bold rounded-pill px-2"
            style={{
              background: "#edf7e1",
              color: "#82c14a",
              fontSize: "0.75rem",
            }}
          >
            {parseFloat(item[valueKey] || 0)}
          </span>
        </div>
      ))}
    </div>
  );

  const QtySectionCompleted = ({
    title,
    icon,
    items,
    valueKey1,
    valueKey2,
  }) => (
    <div
      className="rounded-3 p-3 flex-fill"
      style={{ background: "#f9fafb", border: "1px solid #f0f0f0" }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <i
          className={`bi ${icon}`}
          style={{ color: "#82c14a", fontSize: "0.85rem" }}
        />
        <span
          className="fw-bold"
          style={{ fontSize: "0.8rem", color: "#374151" }}
        >
          {title}
        </span>
      </div>
      {(items || []).map((item) => (
        <div
          key={item.sku}
          className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2 mb-1"
          style={{ background: "#fff", fontSize: "0.8rem" }}
        >
          <span className="text-dark">{item.itemName}</span>
          <span
            className="fw-bold rounded-pill px-2"
            style={{
              background: "#edf7e1",
              color: "#82c14a",
              fontSize: "0.75rem",
            }}
          >
            {parseFloat(item[valueKey1] || 0) +
              parseFloat(item[valueKey2] || 0)}
          </span>
        </div>
      ))}
    </div>
  );

  const showApprove =
    stock?.status === "Pending" ||
    stock?.status === "Pending Partially Received" ||
    stock?.status === "Pending Fully Received" ||
    stock?.status === "For Reconciliation";

  const approveHandler =
    stock?.status === "Pending"
      ? onApproveRequest
      : stock?.status === "Pending Partially Received"
        ? onApprovePartialRequest
        : stock?.status === "Pending Fully Received"
          ? onApproveReceive
          : onApproveReconcile;

  const showDispatch = ["Approved", "Partially Received"].includes(
    stock?.status,
  );
  const showMenu = !["Cancelled", "Disapproved", "Approved"].includes(
    stock?.status,
  );

  return (
    <div
      className="rounded-3 overflow-hidden"
      style={{
        border: "1px solid #e8e8e8",
        background: "#fff",
        transition: "box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-2 d-flex justify-content-between align-items-center"
        style={{
          background: "linear-gradient(135deg, #82c14a 0%, #6ba83a 100%)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i
            className="bi bi-box-seam text-white"
            style={{ fontSize: "1rem" }}
          />
          <span className="text-white fw-bold" style={{ fontSize: "0.9rem" }}>
            {stock?.requestNumber || "N/A"}
          </span>
        </div>
        <span
          className="px-3 py-1 fw-semibold rounded-pill"
          style={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.border}`,
            fontSize: "0.72rem",
          }}
        >
          {stock?.status}
        </span>
      </div>

      {/* Body */}
      <div className="p-3">
        {/* Info rows */}
        <InfoRow
          icon="bi-shop"
          label="Store"
          value={stock?.destinationLocation?.name}
        />
        <InfoRow
          icon="bi-calendar-event"
          label="Request Date"
          value={formatDate(stock?.requestDate)}
        />

        {/* Quantity Cards */}
        {!["Pending", "Approved"].includes(stock?.status) && (
          <div className="d-flex gap-2 mt-3 flex-wrap">
            <QtySection
              title="Dispatched"
              icon="bi-send"
              items={stock?.items}
              valueKey="quantityApprove"
            />
            {stock?.status === "Completed" ? (
              <></>
            ) : (
              <>
                <QtySection
                  title="Received"
                  icon="bi-inbox"
                  items={stock?.items}
                  valueKey="quantityReceive"
                />
                <QtySection
                  title="Remaining"
                  icon="bi-hourglass-split"
                  items={stock?.items}
                  valueKey="remainingQty"
                />
              </>
            )}
            {stock?.status === "Completed" && (
              <QtySectionCompleted
                title="Received"
                icon="bi-inbox"
                items={stock?.items}
                valueKey1="quantityReceive"
                valueKey2="remainingQty"
              />
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        className="px-3 py-2 d-flex gap-2 align-items-center border-top"
        style={{ background: "#fafafa" }}
        ref={menuRef}
      >
        {showApprove && (
          <button
            className="btn btn-sm text-white fw-semibold px-3 rounded-pill"
            style={{
              background: "linear-gradient(135deg, #82c14a 0%, #6ba83a 100%)",
              border: "none",
              fontSize: "0.8rem",
            }}
            onClick={approveHandler}
          >
            <i className="bi bi-check-lg me-1" /> Approve
          </button>
        )}

        {showDispatch && (
          <button
            className="btn btn-sm text-white fw-semibold px-3 rounded-pill"
            style={{
              background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
              border: "none",
              fontSize: "0.8rem",
            }}
            onClick={onDispatchRequest}
          >
            <i className="bi bi-truck me-1" /> Dispatch
          </button>
        )}

        <button
          className="btn btn-sm fw-semibold px-3 rounded-pill"
          style={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            color: "#374151",
            fontSize: "0.8rem",
          }}
          onClick={onEdit}
        >
          <i className="bi bi-eye me-1" /> View
        </button>

        {showMenu && (
          <div className="position-relative ms-auto">
            <button
              className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "32px",
                height: "32px",
                background: showDisapprove ? "#fee2e2" : "#f3f4f6",
                border: "none",
                transition: "background 0.2s ease",
              }}
              onClick={handleToggle}
            >
              <i
                className="bi bi-three-dots-vertical"
                style={{
                  color: showDisapprove ? "#dc2626" : "#6b7280",
                  fontSize: "0.9rem",
                }}
              />
            </button>

            {showDisapprove && (
              <div
                className="position-absolute shadow-sm rounded-3 overflow-hidden"
                style={{
                  bottom: "110%",
                  right: 0,
                  zIndex: 10,
                  animation: "fadeIn 0.15s ease",
                }}
              >
                <button
                  className="btn btn-sm text-white fw-semibold px-4 py-2 d-flex align-items-center gap-2"
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    border: "none",
                    whiteSpace: "nowrap",
                    fontSize: "0.8rem",
                  }}
                  onClick={onClickAction}
                >
                  <i className="bi bi-x-circle" /> Disapprove
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default CollapseView;
